import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Calendar, Plus, Car, Clock, CreditCard, CheckCircle2, XCircle } from "lucide-react";
import { toast, Toaster } from "sonner";

const STATUS_STYLE = {
  pending: "border-yellow-700 text-yellow-300 bg-yellow-950/30",
  confirmed: "border-blue-700 text-blue-300 bg-blue-950/30",
  in_progress: "border-orange-600 text-orange-300 bg-orange-950/30",
  completed: "border-green-700 text-green-300 bg-green-950/30",
  cancelled: "border-zinc-700 text-zinc-400 bg-zinc-900",
};

const STATUS_LABEL = {
  pending: "Pending",
  confirmed: "Confirmed",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [paying, setPaying] = useState({}); // appt_id -> bool
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const loadAppts = useCallback(async () => {
    try {
      const { data } = await api.get("/appointments/me");
      setAppts(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    loadAppts();
  }, [loadAppts]);

  // Handle return from Stripe checkout
  useEffect(() => {
    const sessionId = params.get("session_id");
    const cancelled = params.get("deposit");
    if (cancelled === "cancelled") {
      toast.error("Deposit cancelled — your slot isn't confirmed yet.");
      params.delete("deposit");
      setParams(params, { replace: true });
      return;
    }
    if (!sessionId) return;

    let attempts = 0;
    const maxAttempts = 6;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Deposit received — slot locked in. See you soon.");
          params.delete("session_id");
          setParams(params, { replace: true });
          await loadAppts();
          return;
        }
        if (data.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          params.delete("session_id");
          setParams(params, { replace: true });
          return;
        }
        attempts += 1;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.message("Still processing — refresh in a moment.");
        }
      } catch {
        toast.error("Couldn't verify payment status.");
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => ({
    total: appts.length,
    active: appts.filter((a) => ["pending", "confirmed", "in_progress"].includes(a.status)).length,
    completed: appts.filter((a) => a.status === "completed").length,
  }), [appts]);

  const titleFor = (id) => services.find((s) => s.id === id)?.title || id;

  const payDeposit = async (apptId) => {
    setPaying((p) => ({ ...p, [apptId]: true }));
    try {
      const { data } = await api.post("/payments/checkout/session", {
        appointment_id: apptId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start payment");
      setPaying((p) => ({ ...p, [apptId]: false }));
    }
  };

  return (
    <div data-testid="customer-dashboard" className="bg-background min-h-[calc(100vh-4rem)]">
      <Toaster theme="dark" position="top-center" />
      <section className="border-b border-zinc-900 bg-zinc-950/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-brand-red">Customer dashboard</div>
              <h1 className="mt-3 font-display uppercase text-4xl sm:text-5xl tracking-tighter text-white leading-[0.95]">
                G&apos;day, <span className="text-brand-red">{user?.name?.split(" ")[0] || "driver"}.</span>
              </h1>
              <p className="mt-3 text-zinc-400">Track every service, every step.</p>
            </div>
            <Link data-testid="dashboard-book-btn" to="/book" className="inline-flex items-center gap-2 px-6 py-3 bg-brand-red text-white font-bold uppercase tracking-wider hover:bg-[#B91C2B]">
              <Plus className="h-4 w-4" /> New booking
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
          <StatCard label="Total bookings" value={stats.total} />
          <StatCard label="Active" value={stats.active} highlight />
          <StatCard label="Completed" value={stats.completed} />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display uppercase text-2xl text-white tracking-tight">Your bookings</h2>
        </div>

        {loading ? (
          <div data-testid="appts-loading" className="font-mono text-xs uppercase tracking-widest text-zinc-500 py-12">Loading…</div>
        ) : appts.length === 0 ? (
          <div data-testid="appts-empty" className="border border-zinc-900 bg-zinc-950 p-12 text-center">
            <Car className="h-10 w-10 text-zinc-700 mx-auto" />
            <p className="mt-4 text-zinc-300">No bookings yet.</p>
            <Link to="/book" className="mt-5 inline-flex items-center gap-2 px-6 py-3 bg-brand-red text-white font-bold uppercase tracking-wider hover:bg-[#B91C2B]">
              Book your first service
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-px bg-zinc-900 border border-zinc-900">
            {appts.map((a) => {
              const depositPaid = a.deposit_status === "paid";
              return (
                <article key={a.id} data-testid={`appt-${a.id}`} className="bg-zinc-950 p-6 grid grid-cols-1 md:grid-cols-12 gap-5 items-center">
                  <div className="md:col-span-4">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Service</div>
                    <div className="mt-1 font-display uppercase text-xl text-white tracking-tight">{titleFor(a.service)}</div>
                    <div className="mt-1 text-sm text-zinc-400">{a.vehicle_year} {a.vehicle_make} {a.vehicle_model}{a.vehicle_rego ? ` · ${a.vehicle_rego}` : ""}</div>
                  </div>
                  <div className="md:col-span-3">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">When</div>
                    <div className="mt-1 flex items-center gap-2 text-zinc-200"><Calendar className="h-4 w-4 text-brand-red" /> {a.preferred_date}</div>
                    <div className="flex items-center gap-2 text-zinc-200"><Clock className="h-4 w-4 text-brand-red" /> {a.preferred_time}</div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Deposit</div>
                    <div className="mt-1">
                      {depositPaid ? (
                        <span data-testid={`deposit-paid-${a.id}`} className="inline-flex items-center gap-1.5 text-green-400 text-sm font-medium">
                          <CheckCircle2 className="h-4 w-4" /> Paid
                        </span>
                      ) : (
                        <span data-testid={`deposit-unpaid-${a.id}`} className="inline-flex items-center gap-1.5 text-yellow-400 text-sm font-medium">
                          <XCircle className="h-4 w-4" /> Unpaid
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="md:col-span-3 flex md:justify-end flex-wrap gap-2 items-center">
                    <span data-testid={`appt-status-${a.id}`} className={`inline-flex items-center px-3 py-1.5 border text-[11px] font-mono uppercase tracking-widest ${STATUS_STYLE[a.status] || ""}`}>
                      {STATUS_LABEL[a.status] || a.status}
                    </span>
                    {!depositPaid && a.status !== "cancelled" && (
                      <div className="flex flex-col items-end gap-1">
                        <button
                          data-testid={`pay-deposit-${a.id}`}
                          onClick={() => payDeposit(a.id)}
                          disabled={!!paying[a.id]}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-red text-white text-[11px] font-mono uppercase tracking-widest hover:bg-[#B91C2B] disabled:opacity-60"
                        >
                          <CreditCard className="h-3.5 w-3.5" />
                          {paying[a.id] ? "Loading…" : "Pay $50 deposit"}
                        </button>
                        <div data-testid={`stripe-badge-${a.id}`} className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-zinc-500">
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                          <span>Secured by</span>
                          <span className="font-display tracking-tight text-zinc-300">Stripe</span>
                        </div>
                      </div>
                    )}
                  </div>
                  {a.notes && (
                    <div className="md:col-span-12 text-sm text-zinc-400 border-t border-zinc-900 pt-3">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mr-2">Notes</span>
                      {a.notes}
                    </div>
                  )}
                  {a.admin_note && (
                    <div className="md:col-span-12 text-sm text-red-300/90 border-l-2 border-brand-red pl-3">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-brand-red mr-2">Workshop note</span>
                      {a.admin_note}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

const StatCard = ({ label, value, highlight }) => (
  <div className={`bg-zinc-950 p-6 ${highlight ? "border-l-2 border-brand-red" : ""}`}>
    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
    <div className={`mt-2 font-display text-4xl ${highlight ? "text-brand-red" : "text-white"}`}>{value}</div>
  </div>
);
